/**
 * ChatPanel - Conversation panel
 *
 * `layout="side"`: fixed 420px with left border (desktop split).
 * `layout="full"`: full width (mobile tab layout).
 * `layout="unified"`: mobile ordered stream (timeline: user → assistant → artifacts → SQL approval).
 *
 * Clean conversation thread:
 * - User messages: right-aligned, subtle bg
 * - Assistant messages: left-aligned, no bubble, clean prose
 * - No tool messages (suppressed by stream hook)
 * - Streaming indicator with action label
 * - Input composer at bottom
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Typography,
  TextField,
  IconButton,
  Link,
  Tooltip,
  Skeleton,
  alpha,
  useTheme,
} from '@mui/material';
import { AppDialog } from '../../../components/dialog';
import SendRounded from '@mui/icons-material/SendRounded';
import StopRounded from '@mui/icons-material/StopRounded';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import TimelineOutlined from '@mui/icons-material/TimelineOutlined';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { format as formatSQL } from 'sql-formatter';
import {
  useCopilotStore,
  copilotAuditHasDataTrail,
  type CopilotMessage,
  type CalculationAuditReport,
  type CopilotTimelineEntry,
  type WorkspaceArtifact,
} from '../stores/useCopilotStore';
import { ArtifactCanvas, SQLApprovalCard } from './WorkspacePanel';
import { useCopilotStreamingStatus } from '../hooks/useCopilotStreamingStatus';
import type { CopilotStreamingStatus } from '../hooks/useCopilotStreamingStatus';
import KnowledgePill from './KnowledgePill';
import WelcomeState from './WelcomeState';
import ThreadTabBar from './ThreadTabBar';
import ConnectorTypeIcon from './ConnectorTypeIcon';

function formatSqlForDisplay(sql: string): string {
  if (!sql) return '';
  try {
    return formatSQL(sql, { language: 'sql' });
  } catch {
    return sql;
  }
}

function formatRequestForDisplay(query: string, requestType?: string): string {
  if (!query) return '';
  if ((requestType || '').toLowerCase() === 'api') {
    try {
      const parsed = JSON.parse(query);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return query;
    }
  }
  return formatSqlForDisplay(query);
}

/** Chart intent x/y may be a single string or an array of strings. */
function normalizeChartAxisFields(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.map((v) => String(v)).filter((s) => s.length > 0);
  }
  const s = String(value).trim();
  return s ? [s] : [];
}

interface ChatPanelProps {
  /** `side` = 420px column; `full` = mobile stacked; `unified` = mobile timeline stream */
  layout?: 'side' | 'full' | 'unified';
  messages: CopilotMessage[];
  isStreaming: boolean;
  streamingContent: string;
  streamingAction: string;
  pendingSQLApproval: boolean;
  threadTitle: string | null;
  inputText: string;
  onInputChange: (text: string) => void;
  onSend: () => void;
  onStop: () => void;
  onSuggestedPrompt: (prompt: string) => void;
  /** `layout="unified"`: inline SQL approval + artifacts */
  onSQLApprove?: () => void;
  onSQLEdit?: (editedSql: string) => void;
  onSQLReject?: (reason: string) => void;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  /** Override the side-layout width (used by drag-to-resize). Defaults to 420. */
  panelWidth?: number;
}

export default function ChatPanel({
  layout = 'side',
  messages,
  isStreaming,
  streamingContent,
  streamingAction,
  pendingSQLApproval,
  threadTitle,
  inputText,
  onInputChange,
  panelWidth,
  onSend,
  onStop,
  onSuggestedPrompt,
  onSQLApprove,
  onSQLEdit,
  onSQLReject,
  onAddToDashboard,
}: ChatPanelProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const streamStatus = useCopilotStreamingStatus();
  const [auditOpen, setAuditOpen] = useState(false);
  const [activeAuditId, setActiveAuditId] = useState<string | null>(null);
  const timeline = useCopilotStore((s) => s.timeline);
  const workspaceTabs = useCopilotStore((s) => s.workspaceTabs);
  const pendingSqlDetail = useCopilotStore((s) => s.pendingSQLApproval);
  const isLoadingHistory = useCopilotStore((s) => s.isLoadingHistory);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, streamingAction, streamStatus.primary, streamStatus.toolCallCount, timeline]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSend();
      }
    },
    [onSend],
  );

  const isUnified = layout === 'unified';
  const showWelcome =
    !isLoadingHistory && messages.length === 0 && !streamingContent && (!isUnified || timeline.length === 0);

  const isFull = layout === 'full' || isUnified;

  const renderTimelineEntry = (entry: CopilotTimelineEntry, index: number) => {
    const rowKey = `${index}-${entry.type}`;
    switch (entry.type) {
      case 'user': {
        const msg = messages.find((m) => m.id === entry.messageId);
        if (!msg) return null;
        return (
          <MessageRow
            key={rowKey}
            message={msg}
            onOpenAudit={(auditId) => {
              setActiveAuditId(auditId);
              setAuditOpen(true);
            }}
          />
        );
      }
      case 'assistant': {
        const msg = messages.find((m) => m.id === entry.messageId);
        if (!msg) return null;
        return (
          <MessageRow
            key={rowKey}
            message={msg}
            onOpenAudit={(auditId) => {
              setActiveAuditId(auditId);
              setAuditOpen(true);
            }}
          />
        );
      }
      case 'streaming_assistant': {
        return (
          <Box key={rowKey} sx={{ width: '100%' }}>
            {streamingContent ? (
              <MessageRow
                message={{
                  id: '__streaming__',
                  role: 'assistant',
                  content: streamingContent,
                  timestamp: new Date().toISOString(),
                }}
                onOpenAudit={(auditId) => {
                  setActiveAuditId(auditId);
                  setAuditOpen(true);
                }}
              />
            ) : null}
            {isStreaming && streamStatus.isActive && (
              <StreamingIndicator status={streamStatus} />
            )}
          </Box>
        );
      }
      case 'artifact': {
        const art = workspaceTabs.find((t: WorkspaceArtifact) => t.id === entry.artifactId);
        if (!art) return null;
        return (
          <ArtifactCanvas
            key={entry.artifactId}
            artifact={art}
            variant="inline"
            showSql={false}
            onAddToDashboard={onAddToDashboard}
          />
        );
      }
      case 'sql_approval': {
        const match =
          pendingSqlDetail &&
          pendingSqlDetail.timelineId === entry.id &&
          onSQLApprove &&
          onSQLEdit &&
          onSQLReject;
        if (match) {
          return (
            <SQLApprovalCard
              key={entry.id}
              approval={pendingSqlDetail}
              onApprove={onSQLApprove}
              onEdit={onSQLEdit}
              onReject={onSQLReject}
              variant="inline"
            />
          );
        }
        return (
          <Box key={entry.id} sx={{ py: 0.5, px: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Query approval step (completed)
            </Typography>
          </Box>
        );
      }
      default:
        return null;
    }
  };

  const sideWidth = typeof panelWidth === 'number' && Number.isFinite(panelWidth)
    ? Math.max(280, panelWidth)
    : 420;
  return (
    <Box
      sx={{
        width: isFull ? '100%' : sideWidth,
        minWidth: isFull ? 0 : sideWidth,
        maxWidth: isFull ? '100%' : undefined,
        flex: isFull ? 1 : undefined,
        flexShrink: isFull ? 1 : 0,
        borderLeft: isFull ? 'none' : '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: isDark ? 'background.default' : '#fff',
      }}
    >
      {/* Thread tab bar */}
      <ThreadTabBar threadTitle={threadTitle} isStreaming={isStreaming} />

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 2,
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {showWelcome && <WelcomeState onPromptClick={onSuggestedPrompt} />}

        {isLoadingHistory && <ChatHistorySkeleton />}

        {!isLoadingHistory && (isUnified ? (
          <>
            {timeline.length > 0
              ? timeline.map((entry, i) => renderTimelineEntry(entry, i))
              : messages.map((msg) => (
                  <MessageRow
                    key={msg.id}
                    message={msg}
                    onOpenAudit={(auditId) => {
                      setActiveAuditId(auditId);
                      setAuditOpen(true);
                    }}
                  />
                ))}
            {isStreaming && streamStatus.isActive && !timeline.some((e: CopilotTimelineEntry) => e.type === 'streaming_assistant') && (
              <StreamingIndicator status={streamStatus} />
            )}
          </>
        ) : (
          <>
            {messages.map((msg) => (
              <MessageRow
                key={msg.id}
                message={msg}
                onOpenAudit={(auditId) => {
                  setActiveAuditId(auditId);
                  setAuditOpen(true);
                }}
              />
            ))}

            {streamingContent && (
              <MessageRow
                message={{
                  id: '__streaming__',
                  role: 'assistant',
                  content: streamingContent,
                  timestamp: new Date().toISOString(),
                }}
              />
            )}

            {isStreaming && streamStatus.isActive && (
              <StreamingIndicator status={streamStatus} />
            )}
          </>
        ))}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Composer - elevated */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: isDark ? alpha('#fff', 0.01) : alpha('#000', 0.015),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 0.5,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: isDark ? 'background.paper' : '#fff',
            px: 1.5,
            py: 0.5,
            boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.04)',
            '&:focus-within': {
              borderColor: 'primary.main',
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.12)}`,
            },
            transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={5}
            placeholder={
              pendingSQLApproval
                ? isUnified
                  ? 'Review & approve the SQL above…'
                  : isFull
                    ? 'Review & approve the SQL in the Results tab…'
                    : 'Review & approve the SQL in the workspace panel ←'
                : 'Ask about your data…'
            }
            value={inputText}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isStreaming || pendingSQLApproval}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.85rem',
                lineHeight: 1.5,
                py: 0.5,
              },
            }}
          />
          {isStreaming ? (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3, flexShrink: 0 }}>
              <Tooltip title="Stop">
                <IconButton onClick={onStop} size="small" color="error" sx={{ mr: -0.25 }}>
                  <StopRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Tooltip title="Send (Enter)">
              <span>
                <IconButton
                  onClick={onSend}
                  size="small"
                  disabled={!inputText.trim() || pendingSQLApproval}
                  sx={{
                    mb: 0.3,
                    bgcolor: inputText.trim() ? 'primary.main' : 'transparent',
                    color: inputText.trim() ? '#fff' : 'text.disabled',
                    width: 30, height: 30,
                    '&:hover': { bgcolor: inputText.trim() ? 'primary.dark' : 'transparent' },
                    '&.Mui-disabled': { bgcolor: 'transparent', color: 'text.disabled' },
                    transition: 'all 0.15s ease',
                  }}
                >
                  <SendRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <ContextUsageBar />
      </Box>

      <AuditDialog
        open={auditOpen}
        auditId={activeAuditId}
        onClose={() => {
          setAuditOpen(false);
          setActiveAuditId(null);
        }}
      />
    </Box>
  );
}

// ============================================================================
// Message Row
// ============================================================================

function MessageRow({
  message,
  onOpenAudit,
}: {
  message: CopilotMessage;
  onOpenAudit?: (auditId: string) => void;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isUser = message.role === 'user';
  const auditReport = useCopilotStore((s) =>
    message.auditId ? s.auditReports[message.auditId] : undefined,
  ) as CalculationAuditReport | undefined;
  const showDataTrail = !!message.auditId && copilotAuditHasDataTrail(auditReport);

  // Knowledge captured pill - rendered differently from regular messages
  if (message.type === 'knowledge_captured') {
    return (
      <Box sx={{ width: '100%' }}>
        <KnowledgePill items={message.knowledgeItems || []} />
      </Box>
    );
  }

  // findings are shown in the Key Numbers modal (sidebar), not in chat
  if (message.type === 'finding') return null;

  if (isUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
        <Box
          sx={{
            maxWidth: '80%',
            px: 2,
            py: 1,
            borderRadius: '14px',
            borderBottomRightRadius: '4px',
            bgcolor: isDark ? alpha(theme.palette.primary.main, 0.85) : theme.palette.primary.main,
            color: '#fff',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: '0.85rem', lineHeight: 1.55, whiteSpace: 'pre-wrap', color: '#fff' }}
          >
            {message.content}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Assistant - left-aligned prose with avatar
  return (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
      <Box
        sx={{
          width: 24, height: 24, borderRadius: '6px', flexShrink: 0, mt: 0.25,
          bgcolor: (t: any) => t.palette.mode === 'dark' ? alpha(t.palette.primary.main, 0.15) : alpha(t.palette.primary.main, 0.08),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <SmartToyOutlined sx={{ fontSize: 14, color: 'primary.main' }} />
      </Box>
      <Box sx={{ maxWidth: '100%', flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            '& p': { m: 0, mb: 0.75, fontSize: '0.85rem', lineHeight: 1.65 },
            '& p:last-child': { mb: 0 },
            '& ul, & ol': { mt: 0, mb: 0.75, pl: 2.5, fontSize: '0.85rem' },
            '& li': { mb: 0.25 },
            '& strong': { fontWeight: 600 },
            '& code': {
              fontSize: '0.78rem',
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'grey.100',
              px: 0.5,
              py: 0.1,
              borderRadius: 0.5,
            },
            '& pre': {
              bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'grey.50',
              p: 1.5,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.75rem',
              my: 1,
              '& code': { bgcolor: 'transparent', px: 0, py: 0 },
            },
            '& table': {
              borderCollapse: 'collapse',
              fontSize: '0.78rem',
              my: 1,
              width: '100%',
            },
            '& th, & td': {
              border: '1px solid',
              borderColor: 'divider',
              px: 1,
              py: 0.4,
              textAlign: 'left',
              fontSize: '0.75rem',
            },
            '& th': {
              bgcolor: isDark ? 'grey.800' : 'grey.50',
              fontWeight: 600,
            },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
        </Box>
        {showDataTrail && (
          <Typography
            component="p"
            variant="caption"
            sx={{
              m: 0,
              mt: 1,
              color: 'text.secondary',
              fontSize: '0.78rem',
              lineHeight: 1.55,
              maxWidth: 520,
            }}
          >
            This reply was built from your connected data and workspace steps.{' '}
            <Tooltip
              title="See which sources were queried, SQL or API requests, calculations, and charts."
              placement="top"
              enterDelay={350}
            >
              <Link
                component="button"
                type="button"
                variant="inherit"
                onClick={() => message.auditId && onOpenAudit?.(message.auditId)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.45,
                  verticalAlign: 'text-bottom',
                  fontSize: 'inherit',
                  fontWeight: 600,
                  cursor: 'pointer',
                  p: 0,
                  border: 'none',
                  background: 'none',
                  fontFamily: 'inherit',
                  textDecoration: 'none',
                  color: 'primary.main',
                  '&:hover': {
                    color: 'primary.dark',
                    '& .audit-trail-link-text': { textDecorationColor: 'currentColor' },
                  },
                }}
              >
                <TimelineOutlined sx={{ fontSize: '1.05rem', flexShrink: 0, opacity: 0.92 }} />
                <Box
                  component="span"
                  className="audit-trail-link-text"
                  sx={{
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    textDecorationColor: (t) => alpha(t.palette.primary.main, 0.45),
                  }}
                >
                  View sources and calculation trail
                </Box>
              </Link>
            </Tooltip>
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function AuditDialog({ open, auditId, onClose }: { open: boolean; auditId: string | null; onClose: () => void }) {
  const theme = useTheme();
  const report = useCopilotStore((s: any) => (auditId ? s.auditReports[auditId] : null)) as CalculationAuditReport | null;
  const threadTitle = useCopilotStore((s) => s.threadTitle);
  const [copiedQueryKey, setCopiedQueryKey] = useState<string>('');

  useEffect(() => {
    setCopiedQueryKey('');
  }, [auditId, open]);

  const handleCopyQuery = useCallback((sql: string, key: string) => {
    if (!sql) return;
    navigator.clipboard.writeText(sql);
    setCopiedQueryKey(key);
    setTimeout(() => setCopiedQueryKey(''), 1800);
  }, []);

  const auditPanelBg =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.04)
      : alpha(theme.palette.common.black, 0.03);
  const sourceCardBg =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.06)
      : theme.palette.background.paper;
  const sourceCardOutline =
    theme.palette.mode === 'dark'
      ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
      : `1px solid ${alpha(theme.palette.common.black, 0.06)}`;

  const stepHeader = (n: number, label: string) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.25 }}>
      <Box
        sx={(t) => ({
          minWidth: 22,
          height: 22,
          borderRadius: `${t.ds.radius.sm}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700,
          bgcolor: t.tokens.brand.primarySoft,
          color: t.tokens.brand.primary,
        })}
      >
        {n}
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: '-0.01em' }}>
        {label}
      </Typography>
    </Box>
  );

  const queryAccordionBg =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.07)
      : theme.palette.grey[100];
  const queryAccordionHoverBg =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.1)
      : theme.palette.grey[200];
  const hasForecastDetails = !!report?.story?.outputs?.forecastDetails?.length;
  const resultsStepNumber = hasForecastDetails ? 5 : 4;
  const auditAccordionSx = {
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.8 : 1)}`,
    borderRadius: 1,
    overflow: 'hidden',
    boxShadow: 'none',
    bgcolor: queryAccordionBg,
    '&:before': { display: 'none' },
    '& .MuiAccordionSummary-root': {
      bgcolor: queryAccordionBg,
      color: 'text.primary',
      '&:hover:not(.Mui-disabled)': { bgcolor: queryAccordionHoverBg },
    },
  } as const;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="md"
      icon={<TimelineOutlined sx={{ color: 'primary.main', opacity: 0.92 }} />}
      title="Sources and calculation trail"
      dividers
    >
        {!report ? (
          <Typography variant="body2" color="text.secondary">
            No audit details available for this response.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.75 }}>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.75, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
              >
                Thread
              </Typography>
              <Box
                sx={{
                  color: 'text.primary',
                  fontSize: '1rem',
                  fontWeight: 500,
                  lineHeight: 1.45,
                  '& p': { m: 0 },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {(threadTitle && threadTitle.trim()) || report.question || ''}
                </ReactMarkdown>
              </Box>
            </Box>

            {report.story ? (
              <>
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.75, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
                  >
                    Summary
                  </Typography>
                  {report.story.headline ? (
                    <Box
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        lineHeight: 1.65,
                        fontWeight: 400,
                        '& p': { m: 0 },
                        '& strong': { color: 'text.primary', fontWeight: 600 },
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.story.headline}</ReactMarkdown>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No summary for this run.
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2.25,
                    bgcolor: auditPanelBg,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                  }}
                >
                  <Box sx={{ pb: 2 }}>
                    {stepHeader(1, 'Data sources')}
                    {report.story.dataSources?.summary
                      && !report.story.dataSources?.details?.length && (
                      <Box sx={{ color: 'text.secondary', fontSize: '0.8125rem', '& p': { m: 0 }, mb: 1 }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.story.dataSources.summary}</ReactMarkdown>
                      </Box>
                    )}
                    {!!report.story.dataSources?.details?.length && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {report.story.dataSources.details.map((d, idx) => (
                          <Box
                            key={`${d.name}-${idx}`}
                            sx={{
                              p: 1.5,
                              borderRadius: 1.5,
                              bgcolor: sourceCardBg,
                              boxShadow: 'none',
                              border: sourceCardOutline,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                              <ConnectorTypeIcon connectorType={d.connectorType} size={22} />
                              <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                                {d.name || 'Source'}
                                {d.connectorType ? (
                                  <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                                    {` · ${d.connectorType}`}
                                  </Typography>
                                ) : null}
                              </Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={(d.requestType || 'sql').toUpperCase()}
                                sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.04em' }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                              {(d.records || 0).toLocaleString()} rows · {(d.columns || 0).toLocaleString()} columns
                            </Typography>
                            {!!d.query && (
                              <Accordion disableGutters sx={auditAccordionSx}>
                                <AccordionSummary expandIcon={<ExpandMoreRounded fontSize="small" />} sx={{ minHeight: 40, px: 1.25 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                      {(d.requestType || 'sql').toLowerCase() === 'api' ? 'API request' : 'SQL query'}
                                    </Typography>
                                    <Tooltip title={copiedQueryKey === `src-${idx}` ? 'Copied!' : 'Copy'}>
                                      <IconButton
                                        size="small"
                                        sx={{ ml: 'auto', mr: 0.5 }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopyQuery(
                                            formatRequestForDisplay(d.query || '', d.requestType || 'sql'),
                                            `src-${idx}`,
                                          );
                                        }}
                                      >
                                        {copiedQueryKey === `src-${idx}` ? (
                                          <CheckCircleRounded fontSize="small" color="success" />
                                        ) : (
                                          <ContentCopyRounded fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails
                                  sx={{
                                    p: 0,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.2) : theme.palette.grey[50],
                                  }}
                                >
                                  <SyntaxHighlighter
                                    language={((d.requestType || 'sql').toLowerCase() === 'api') ? 'json' : 'sql'}
                                    style={theme.palette.mode === 'dark' ? vscDarkPlus : vs}
                                    customStyle={{
                                      margin: 0,
                                      padding: '10px 12px',
                                      fontSize: '0.75rem',
                                      borderRadius: 0,
                                      ...(theme.palette.mode === 'light'
                                        ? { background: `${theme.palette.grey[50]} !important` as const }
                                        : {}),
                                    }}
                                  >
                                    {formatRequestForDisplay(d.query || '', d.requestType || 'sql')}
                                  </SyntaxHighlighter>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ borderColor: (t) => alpha(t.palette.divider, 0.7) }} />

                  <Box sx={{ py: 2 }}>
                    {stepHeader(2, 'Fetched and prepared in workspace')}
                    {report.story.staging?.summary && (
                      <Box sx={{ color: 'text.secondary', fontSize: '0.8125rem', '& p': { m: 0 } }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.story.staging.summary}</ReactMarkdown>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ borderColor: (t) => alpha(t.palette.divider, 0.7) }} />

                  <Box sx={{ py: 2 }}>
                    {stepHeader(3, 'Calculations')}
                    {report.story.calculation?.summary && (
                      <Box sx={{ color: 'text.secondary', fontSize: '0.8125rem', '& p': { m: 0 }, mb: 1 }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.story.calculation.summary}</ReactMarkdown>
                      </Box>
                    )}
                    {!!report.story.calculation?.steps?.length && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {report.story.calculation.steps.map((step, idx) => {
                          const qKey = `calc-${idx}`;
                          return (
                            <Accordion key={`${idx}-${step.tool}`} disableGutters sx={auditAccordionSx}>
                              <AccordionSummary expandIcon={<ExpandMoreRounded fontSize="small" />} sx={{ minHeight: 40, px: 1.25 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Workspace query {idx + 1}
                                  </Typography>
                                  {!!step.sql && (
                                    <Tooltip title={copiedQueryKey === qKey ? 'Copied!' : 'Copy query'}>
                                      <IconButton
                                        size="small"
                                        sx={{ ml: 'auto', mr: 0.5 }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopyQuery(step.sql || '', qKey);
                                        }}
                                      >
                                        {copiedQueryKey === qKey ? (
                                          <CheckCircleRounded fontSize="small" color="success" />
                                        ) : (
                                          <ContentCopyRounded fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails
                                sx={{
                                  p: 0,
                                  borderTop: 1,
                                  borderColor: 'divider',
                                  bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.2) : theme.palette.grey[50],
                                }}
                              >
                                <SyntaxHighlighter
                                  language="sql"
                                  style={theme.palette.mode === 'dark' ? vscDarkPlus : vs}
                                  customStyle={{
                                    margin: 0,
                                    padding: '10px 12px',
                                    fontSize: '0.75rem',
                                    borderRadius: 0,
                                    ...(theme.palette.mode === 'light'
                                      ? { background: `${theme.palette.grey[50]} !important` as const }
                                      : {}),
                                  }}
                                >
                                  {formatSqlForDisplay(step.sql || '')}
                                </SyntaxHighlighter>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </Box>
                    )}
                  </Box>

                  {hasForecastDetails && (
                    <>
                      <Divider sx={{ borderColor: (t) => alpha(t.palette.divider, 0.7) }} />
                      <Box sx={{ py: 2 }}>
                        {stepHeader(4, 'Forecast model report')}
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.6, mb: 1 }}>
                          We backtested multiple models on recent history and selected the best one using a weighted score (RMSE + MAE + WAPE + tail error).
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {report.story.outputs?.forecastDetails?.map((fc, fidx) => {
                            const fp = fc.firstPeriod;
                            const conf =
                              fc.confidenceLevelPct != null
                                ? `${fc.confidenceLevelPct}%`
                                : fc.confidenceLevel != null
                                  ? `${Math.round(fc.confidenceLevel * 100)}%`
                                  : null;
                            const candidates = (fc.candidateModels || []).filter((m) => !!m.model);
                            return (
                              <Box
                                key={`${fc.title}-${fidx}`}
                                sx={{
                                  p: 1.25,
                                  borderRadius: 1.5,
                                  bgcolor: sourceCardBg,
                                  border: sourceCardOutline,
                                }}
                              >
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {fc.title || `Forecast ${fidx + 1}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.55 }}>
                                  {fc.selectedModel ? `Selected model: ${fc.selectedModel}` : 'Forecast model'}
                                  {fc.horizon != null && fc.horizon > 0 && fc.timeUnit
                                    ? ` · ${fc.horizon} ${fc.timeUnit}${fc.horizon === 1 ? '' : 's'} ahead`
                                    : ''}
                                  {conf ? ` · ${conf} interval` : ''}
                                </Typography>
                                {!!fc.reliability && (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block">
                                    Reliability: {fc.reliability}
                                    {fc.historyPoints ? ` · based on ${fc.historyPoints.toLocaleString()} history points` : ''}
                                  </Typography>
                                )}
                                {fc.selectedModelMetrics && (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block" sx={{ mt: 0.3 }}>
                                    Selected metrics:
                                    {fc.selectedModelMetrics.selectionScore != null ? ` score ${Number(fc.selectedModelMetrics.selectionScore).toFixed(3)}` : ''}
                                    {fc.selectedModelMetrics.rmse != null ? ` · RMSE ${Number(fc.selectedModelMetrics.rmse).toFixed(3)}` : ''}
                                    {fc.selectedModelMetrics.mae != null ? ` · MAE ${Number(fc.selectedModelMetrics.mae).toFixed(3)}` : ''}
                                    {fc.selectedModelMetrics.wape != null ? ` · WAPE ${(Number(fc.selectedModelMetrics.wape) * 100).toFixed(1)}%` : ''}
                                  </Typography>
                                )}
                                {!!candidates.length && (
                                  <Box sx={{ mt: 0.8, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                      Backtests
                                    </Typography>
                                    {candidates.map((m, midx) => {
                                      const isWinner = !!fc.selectedModel && m.model === fc.selectedModel;
                                      return (
                                        <Box
                                          key={`${m.model}-${midx}`}
                                          sx={{
                                            px: 0.8,
                                            py: 0.55,
                                            borderRadius: 1,
                                            border: `1px solid ${isWinner ? alpha(theme.palette.primary.main, 0.45) : alpha(theme.palette.divider, 0.75)}`,
                                            bgcolor: isWinner ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08) : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.9,
                                            flexWrap: 'wrap',
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ fontWeight: isWinner ? 700 : 600, color: isWinner ? 'primary.main' : 'text.primary' }}>
                                            {m.model}
                                          </Typography>
                                          {m.selectionScore != null && (
                                            <Typography variant="caption" color="text.secondary">score {Number(m.selectionScore).toFixed(3)}</Typography>
                                          )}
                                          {m.rmse != null && (
                                            <Typography variant="caption" color="text.secondary">RMSE {Number(m.rmse).toFixed(3)}</Typography>
                                          )}
                                          {m.mae != null && (
                                            <Typography variant="caption" color="text.secondary">MAE {Number(m.mae).toFixed(3)}</Typography>
                                          )}
                                          {m.wape != null && (
                                            <Typography variant="caption" color="text.secondary">WAPE {(Number(m.wape) * 100).toFixed(1)}%</Typography>
                                          )}
                                          {m.p95AbsError != null && (
                                            <Typography variant="caption" color="text.secondary">P95 {Number(m.p95AbsError).toFixed(3)}</Typography>
                                          )}
                                          {isWinner && (
                                            <Chip size="small" label="Selected" color="primary" sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.02em' }} />
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                )}
                                {(fc.timeColumn || fc.targetColumn || fc.dataWindowStart || fc.dataWindowEnd) && (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block" sx={{ mt: 0.45 }}>
                                    {fc.timeColumn ? `Time: ${fc.timeColumn}` : ''}
                                    {fc.timeColumn && fc.targetColumn ? ' · ' : ''}
                                    {fc.targetColumn ? `Target: ${fc.targetColumn}` : ''}
                                    {(fc.timeColumn || fc.targetColumn) && (fc.dataWindowStart || fc.dataWindowEnd) ? ' · ' : ''}
                                    {(fc.dataWindowStart || fc.dataWindowEnd)
                                      ? `Data window: ${fc.dataWindowStart || '?'} to ${fc.dataWindowEnd || '?'}`
                                      : ''}
                                  </Typography>
                                )}
                                {(fc.droppedPoints || fc.rawRows || fc.preparedPoints || fc.seasonLength) ? (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block" sx={{ mt: 0.3 }}>
                                    Assumptions:
                                    {fc.rawRows ? ` raw rows ${fc.rawRows.toLocaleString()}` : ''}
                                    {fc.preparedPoints ? `, prepared points ${fc.preparedPoints.toLocaleString()}` : ''}
                                    {fc.droppedPoints ? `, dropped ${fc.droppedPoints.toLocaleString()}` : ''}
                                    {fc.seasonLength ? `, season length ${fc.seasonLength}` : ''}
                                  </Typography>
                                ) : null}
                                {!!fc.caveats?.length && (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block" sx={{ mt: 0.3 }}>
                                    Caveats: {fc.caveats.join(' ')}
                                  </Typography>
                                )}
                                {fp && (fp.prediction != null || fp.period != null) && (
                                  <Typography variant="caption" color="text.secondary" component="div" display="block" sx={{ mt: 0.35 }}>
                                    Next step
                                    {fp.period != null && fp.period !== '' ? ` (${fp.period})` : ''}
                                    {fp.prediction != null
                                      ? `: ${fp.prediction}${fp.lower != null && fp.upper != null ? ` (range ${fp.lower}–${fp.upper})` : ''}`
                                      : ''}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </>
                  )}

                  <Divider sx={{ borderColor: (t) => alpha(t.palette.divider, 0.7) }} />

                  <Box sx={{ pt: 2 }}>
                    {stepHeader(resultsStepNumber, 'Results')}
                    {hasForecastDetails ? (
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.6, mb: 1 }}>
                        Final visual/table outputs from the selected forecast model are shown here.
                      </Typography>
                    ) : (
                      report.story.outputs?.summary && (
                        <Box sx={{ color: 'text.secondary', fontSize: '0.8125rem', '& p': { m: 0 }, mb: 1 }}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.story.outputs.summary}</ReactMarkdown>
                        </Box>
                      )
                    )}
                    {!!report.story.outputs?.chartDetails?.length && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {report.story.outputs.chartDetails.map((ch, idx) => {
                          const xFields = normalizeChartAxisFields(ch.xFields);
                          const yFields = normalizeChartAxisFields(ch.yFields);
                          return (
                            <Box
                              key={`${ch.title}-${idx}`}
                              sx={{
                                p: 1.25,
                                borderRadius: 1.5,
                                bgcolor: sourceCardBg,
                                border: sourceCardOutline,
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {ch.title || `Chart ${idx + 1}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.5 }}>
                                {ch.type || 'chart'} · {(ch.records || 0).toLocaleString()} rows
                                {ch.timeUnit ? ` · ${ch.timeUnit}` : ''}
                              </Typography>
                              {xFields.length > 0 && (
                                <Typography variant="caption" color="text.secondary" component="div" display="block">
                                  X: {xFields.join(', ')}
                                </Typography>
                              )}
                              {yFields.length > 0 && (
                                <Typography variant="caption" color="text.secondary" component="div" display="block">
                                  Y: {yFields.join(', ')}
                                </Typography>
                              )}
                              {!!ch.columns?.length && (
                                <Typography variant="caption" color="text.secondary" component="div" display="block">
                                  Fields: {ch.columns.join(', ')}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    {hasForecastDetails && !report.story.outputs?.chartDetails?.length && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        No chart artifact was generated for this run. Output type: {(report.story.outputs?.resultTypes || ['table']).join(', ')}.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No summarized story available yet for this run.
              </Typography>
            )}
          </Box>
        )}
    </AppDialog>
  );
}

// ============================================================================
// Streaming Indicator
// ============================================================================

function ContextUsageBar() {
  const contextUsage = useCopilotStore((s: any) => s.contextUsage) as {
    used: number; budget: number; pct: number; trimmed: boolean; trimmedCount: number;
  } | null;

  if (!contextUsage?.trimmed) return null;

  return (
    <Typography
      variant="caption"
      sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', mt: 0.5, textAlign: 'center', fontStyle: 'italic' }}
    >
      Earlier messages have been summarized to keep the conversation flowing
    </Typography>
  );
}

// ============================================================================
// Streaming Indicator
// ============================================================================

function StreamingIndicator({ status }: { status: CopilotStreamingStatus }) {
  const { primary, staleSubtitle, elapsedSec } = status;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.25,
        pl: 0.25,
        py: 0.5,
        // Center icon against the primary line. When a subtitle is present the
        // text block grows to two lines and the icon naturally centers against
        // both — which is exactly what we want.
        alignItems: staleSubtitle ? 'flex-start' : 'center',
      }}
    >
      {/* Bot icon with a soft pulsing halo to signal active thinking. */}
      <Box
        sx={{
          position: 'relative',
          width: 24,
          height: 24,
          flexShrink: 0,
          // Slight nudge only when the row is two lines, so the icon visually
          // anchors to the first (primary) line instead of the midpoint.
          mt: staleSubtitle ? 0.15 : 0,
        }}
      >
        {/* Outer pulse ring — expands and fades */}
        <Box
          aria-hidden
          sx={(t) => ({
            position: 'absolute',
            inset: 0,
            borderRadius: `${t.ds.radius.sm}px`,
            bgcolor: t.tokens.brand.primarySoft,
            '@keyframes copilotThinkPulse': {
              '0%':   { transform: 'scale(1)',    opacity: 0.55 },
              '70%':  { transform: 'scale(1.6)',  opacity: 0 },
              '100%': { transform: 'scale(1.6)',  opacity: 0 },
            },
            animation: 'copilotThinkPulse 1.6s ease-out infinite',
          })}
        />
        {/* Solid icon tile sits above the pulse */}
        <Box
          sx={(t) => ({
            position: 'relative',
            width: 24,
            height: 24,
            borderRadius: `${t.ds.radius.sm}px`,
            bgcolor: t.tokens.brand.primarySoft,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <SmartToyOutlined sx={{ fontSize: 14, color: 'primary.main' }} />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          component="div"
          color="text.primary"
          sx={{
            fontSize: '0.8rem',
            lineHeight: 1.4,
            fontWeight: 600,
            letterSpacing: '0.01em',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            // Gentle "breath" on the primary text — subtle, not distracting.
            '@keyframes copilotThinkBreath': {
              '0%, 100%': { opacity: 1 },
              '50%':      { opacity: 0.6 },
            },
            '& > .copilot-think-primary': {
              animation: 'copilotThinkBreath 1.6s ease-in-out infinite',
            },
          }}
        >
          <Box component="span" className="copilot-think-primary">
            {primary}
          </Box>
          <Box
            component="span"
            sx={{
              color: 'text.secondary',
              fontWeight: 400,
              ml: 0.75,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            · {elapsedSec}s
          </Box>
        </Typography>
        {staleSubtitle && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              color: 'text.secondary',
              display: 'block',
              mt: 0.4,
              lineHeight: 1.5,
            }}
          >
            {staleSubtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Chat history skeleton ─────────────────────────────────────────────────────
function ChatHistorySkeleton() {
  const theme = useTheme();
  const subtle = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, px: 0.5, pt: 1 }}>
      {/* User message — right-aligned, short */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Skeleton variant="rounded" width="52%" height={38}
          sx={{ borderRadius: 2.5, bgcolor: subtle }} animation="wave" />
      </Box>

      {/* AI response — left-aligned, multi-line */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Skeleton variant="rounded" width="88%" height={16} sx={{ borderRadius: 1, bgcolor: subtle }} animation="wave" />
        <Skeleton variant="rounded" width="76%" height={16} sx={{ borderRadius: 1, bgcolor: subtle }} animation="wave" />
        <Skeleton variant="rounded" width="60%" height={16} sx={{ borderRadius: 1, bgcolor: subtle }} animation="wave" />
      </Box>

      {/* User message — right-aligned, medium */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Skeleton variant="rounded" width="38%" height={38}
          sx={{ borderRadius: 2.5, bgcolor: subtle }} animation="wave" />
      </Box>

      {/* AI response — left-aligned, longer */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Skeleton variant="rounded" width="92%" height={16} sx={{ borderRadius: 1, bgcolor: subtle }} animation="wave" />
        <Skeleton variant="rounded" width="84%" height={16} sx={{ borderRadius: 1, bgcolor: subtle }} animation="wave" />
        <Skeleton variant="rounded" width="70%" height={16} sx={{ borderRadius: 1, bgcolor: subtle }} animation="wave" />
        <Skeleton variant="rounded" width="48%" height={16} sx={{ borderRadius: 1, bgcolor: subtle }} animation="wave" />
      </Box>

      {/* User message — right-aligned, long */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Skeleton variant="rounded" width="65%" height={38}
          sx={{ borderRadius: 2.5, bgcolor: subtle }} animation="wave" />
      </Box>
    </Box>
  );
}
